import type { ReactNode } from "react";

import { useGo } from "../lib/useGo";
import { I } from "./icons";

/**
 * A heading and one bordered group of rows, divided by 1px rules (SNIPPETS §10).
 *
 * When to use: every list — settings hubs, today's rows, reference lists. Children are `NavRow`s
 * and `DiscloseRow`s. The heading is 2–4 words, sentence case.
 */
export function RowGroup({
  heading,
  children,
  ariaLabel,
}: {
  heading?: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <section className="k-group" aria-label={ariaLabel}>
      {heading ? <h2 className="k-group__h">{heading}</h2> : null}
      <div className="k-rows">{children}</div>
    </section>
  );
}

export type RowTone = "default" | "danger" | "brand" | "caution" | "sky" | "cycle" | "success";

interface RowBase {
  title: ReactNode;
  /** 13.8 muted under the title */
  subtitle?: ReactNode;
  /** a 20px icon, shown in the 32px tile */
  icon?: ReactNode;
  /** the tile's tint; `danger` rows default to danger */
  tone?: RowTone;
}

export type NavRowProps = RowBase &
  (
    | {
        /** link: a chevron; navigates with `useGo` — across tabs it switches tab rather than stacking */
        kind?: "link";
        to?: string;
        onClick?: () => void;
        /** the current value, 15 muted tabular, before the chevron */
        value?: ReactNode;
        /** a control between the value and the chevron — Food's Water row carries "+ glass".
         *  The row then splits: the text is the link, the control is its own button. */
        trail?: ReactNode;
      }
    | {
        /** value: a fact with no action — no chevron, not focusable */
        kind: "value";
        value?: ReactNode;
      }
    | {
        kind: "toggle";
        checked: boolean;
        onChange: (checked: boolean) => void;
        disabled?: boolean;
      }
    | {
        /** action: a control at the right, usually a ghost `Button` */
        kind: "action";
        action: ReactNode;
      }
    | {
        /** danger: destructive; the title names exactly what it removes */
        kind: "danger";
        onClick: () => void;
      }
    | {
        /** disabled: muted title, the reason as `subtitle`, a lock at the right */
        kind: "disabled";
      }
  );

/**
 * A value too long for its row wraps, but only between its " · " parts: "207 kcal" or "2/6 this
 * week" split across two lines reads as two values, and an ellipsis hid the figure outright.
 */
function keepParts(v: ReactNode): ReactNode {
  if (typeof v !== "string") return v;
  return v
    .split(" · ")
    .map((part) => part.replace(/ /g, "\u00a0"))
    .join(" · ");
}

function Tile({ icon, tone, muted }: { icon: ReactNode; tone: RowTone; muted?: boolean }) {
  const cls = `k-row__tile${tone !== "default" ? ` k-row__tile--${tone}` : ""}${muted ? " k-row__tile--muted" : ""}`;
  return (
    <span className={cls} aria-hidden="true">
      {icon}
    </span>
  );
}

function Txt({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <span className="k-row__txt">
      <span className="k-row__title">{title}</span>
      {subtitle !== undefined && subtitle !== null ? <span className="k-row__sub">{subtitle}</span> : null}
    </span>
  );
}

/**
 * One row of a RowGroup: [icon tile] · title / subtitle · value · chevron or control.
 * 52px minimum (56 with a tile), 12 16 padding. See `NavRowProps` for the kinds.
 */
export function NavRow(props: NavRowProps) {
  const navigate = useGo();
  const { title, subtitle, icon } = props;
  const kind = props.kind ?? "link";
  const tone: RowTone = props.tone ?? (kind === "danger" ? "danger" : "default");
  const tile = icon ? <Tile icon={icon} tone={tone} muted={kind === "disabled"} /> : null;

  switch (props.kind) {
    case "value":
      return (
        <div className="k-row k-row--value">
          {tile}
          <Txt title={title} subtitle={subtitle} />
          {props.value !== undefined ? <span className="k-row__value">{keepParts(props.value)}</span> : null}
        </div>
      );
    case "toggle":
      return (
        <div className="k-row k-row--toggle">
          {tile}
          <Txt title={title} subtitle={subtitle} />
          <label className="k-row__switch">
            <span className="tog">
              <input
                type="checkbox"
                role="switch"
                checked={props.checked}
                disabled={props.disabled}
                onChange={(e) => props.onChange(e.target.checked)}
                aria-label={typeof title === "string" ? title : undefined}
              />
              <i />
            </span>
          </label>
        </div>
      );
    case "action":
      return (
        <div className="k-row k-row--action">
          {tile}
          <Txt title={title} subtitle={subtitle} />
          {props.action}
        </div>
      );
    case "danger":
      return (
        <button type="button" className="k-row k-row--danger" onClick={props.onClick}>
          {tile}
          <Txt title={title} subtitle={subtitle} />
        </button>
      );
    case "disabled":
      return (
        <button type="button" className="k-row k-row--disabled" disabled>
          {tile}
          <Txt title={title} subtitle={subtitle} />
          <span className="k-row__end" aria-hidden="true">
            {I.lock}
          </span>
        </button>
      );
    default: {
      const go = props.onClick ?? (props.to ? () => navigate(props.to as string) : undefined);
      const value = props.value !== undefined ? <span className="k-row__value">{keepParts(props.value)}</span> : null;
      // With nowhere to go it is a value row — a chevron that leads nowhere is a lie.
      if (!go)
        return (
          <div className="k-row k-row--value">
            {tile}
            <Txt title={title} subtitle={subtitle} />
            {value}
          </div>
        );
      // A button cannot hold a button, so a row with a control is a div of two: the link and the
      // control. The chevron repeats the link for the eye only (kit §37, FoodWater).
      if (props.trail)
        return (
          <div className="k-row k-row--split">
            <button type="button" className="k-row__main" onClick={go}>
              {tile}
              <Txt title={title} subtitle={subtitle} />
              {value}
            </button>
            {props.trail}
            <span className="k-row__end k-row__end--hit" aria-hidden="true" onClick={go}>
              {I["chevron-right"]}
            </span>
          </div>
        );
      return (
        <button type="button" className="k-row k-row--link" onClick={go}>
          {tile}
          <Txt title={title} subtitle={subtitle} />
          {value}
          <span className="k-row__end" aria-hidden="true">
            {I["chevron-right"]}
          </span>
        </button>
      );
    }
  }
}

/**
 * A NavRow that expands in place — native `<details>`, so it is keyboard operable and announced
 * correctly (the reasoning of `Disclose`, in the v2 row look).
 *
 * When to use: inside a RowGroup, for "How this is worked out" and any section that the row's
 * `value` already answers for most visits. `flush` drops the body's indent under the icon tile.
 */
export function DiscloseRow({
  title,
  subtitle,
  value,
  icon,
  tone = "default",
  defaultOpen,
  flush,
  children,
}: RowBase & {
  value?: ReactNode;
  defaultOpen?: boolean;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="k-disclose" open={defaultOpen}>
      <summary className="k-row">
        {icon ? <Tile icon={icon} tone={tone} /> : null}
        <Txt title={title} subtitle={subtitle} />
        {value !== undefined ? <span className="k-row__value">{keepParts(value)}</span> : null}
        <span className="k-row__end" aria-hidden="true">
          {I["chevron-down"]}
        </span>
      </summary>
      <div className={`k-disclose__body${flush || !icon ? " k-disclose__body--flush" : ""}`}>{children}</div>
    </details>
  );
}
